<template>
  <!-- Approval Modal -->
  <div class="modal fade" id="approval-modal" tabindex="-1" role="dialog" aria-labelledby="approvalModalLabel"
       aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="approvalModalLabel">
            <i class="bx bx-check-shield me-2"></i>Hygiene Permit Approval
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Business Summary -->
          <div class="alert alert-light border mb-4">
            <div class="row">
              <div class="col-md-6">
                <h6 class="mb-3"><i class="bx bx-building me-2 text-primary"></i>Business Information</h6>
                <div class="mb-2">
                  <small class="text-muted">Business ID:</small>
                  <strong class="d-block">{{ businessDetails.businessID }}</strong>
                </div>
                <div class="mb-2">
                  <small class="text-muted">Business Name:</small>
                  <strong class="d-block text-capitalize">{{ businessDetails.businessName }}</strong>
                </div>
                <div class="mb-2">
                  <small class="text-muted">Category:</small>
                  <strong class="d-block">{{ businessDetails.businessCategory }}</strong>
                </div>
              </div>
              <div class="col-md-6">
                <h6 class="mb-3"><i class="bx bx-receipt me-2 text-success"></i>Invoice Details</h6>
                <div class="mb-2">
                  <small class="text-muted">Invoice No:</small>
                  <strong class="d-block">{{ billDetails.billNo }}</strong>
                </div>
                <div class="mb-2">
                  <small class="text-muted">Amount:</small>
                  <strong class="d-block text-success">KES {{ numberFormat(billDetails.billTotal) }}</strong>
                </div>
                <div class="mb-2">
                  <small class="text-muted">Balance:</small>
                  <strong class="d-block" :class="billDetails.billBalance > 0 ? 'text-danger' : 'text-success'">
                    KES {{ numberFormat(billDetails.billBalance) }}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Owner Information -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0"><i class="bx bx-user me-2"></i>Owner Information</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Owner Name</label>
                  <div class="fw-semibold">{{ businessDetails.fullNames || businessDetails.owner }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">ID Number</label>
                  <div class="fw-semibold">{{ businessDetails.ownerID }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Phone Number</label>
                  <div class="fw-semibold">{{ businessDetails.ownerPhone }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Email</label>
                  <div class="fw-semibold">{{ businessDetails.ownerEmail }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Location Details -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0"><i class="bx bx-map me-2"></i>Location Details</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Sub County</label>
                  <div class="fw-semibold">{{ businessDetails.subCountyName }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="text-muted small">Ward</label>
                  <div class="fw-semibold">{{ businessDetails.wardName }}</div>
                </div>
                <div class="col-12">
                  <label class="text-muted small">Physical Address</label>
                  <div class="fw-semibold">{{ businessDetails.physicalAddress }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Status Alert -->
          <div v-if="billDetails.billBalance > 0" class="alert alert-warning" role="alert">
            <div class="d-flex align-items-center">
              <i class="bx bx-error-circle font-size-24 me-3"></i>
              <div>
                <h6 class="alert-heading mb-1">Payment Incomplete</h6>
                <p class="mb-0">This business has an outstanding balance of <strong>KES {{ numberFormat(billDetails.billBalance) }}</strong>.
                  Please verify payment status before approving.</p>
              </div>
            </div>
          </div>

          <div v-else class="alert alert-success" role="alert">
            <div class="d-flex align-items-center">
              <i class="bx bx-check-circle font-size-24 me-3"></i>
              <div>
                <h6 class="alert-heading mb-1">Payment Complete</h6>
                <p class="mb-0">All payments have been received. You can proceed with approval.</p>
              </div>
            </div>
          </div>

          <!-- Comments Section -->
          <div class="form-group">
            <label for="comments" class="fw-semibold mb-2">
              <i class="bx bx-message-square-edit me-2"></i>Comments / Remarks
              <span class="text-danger">*</span>
            </label>
            <textarea
                id="comments"
                v-model="localComments"
                @input="$emit('update-comments', localComments)"
                class="form-control"
                rows="4"
                placeholder="Enter your comments, observations, or reasons for approval/decline..."
            ></textarea>
            <small class="text-muted">
              <i class="bx bx-info-circle me-1"></i>
              Comments are required for decline. For approval, please add any relevant notes or observations.
            </small>
          </div>

          <!-- Approval Checklist (Optional) -->
          <div class="card border-0 bg-light mt-4">
            <div class="card-body">
              <h6 class="mb-3"><i class="bx bx-list-check me-2 text-primary"></i>Pre-Approval Checklist</h6>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" id="check1">
                <label class="form-check-label" for="check1">
                  Business details verified and accurate
                </label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" id="check2">
                <label class="form-check-label" for="check2">
                  Payment status confirmed
                </label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" id="check3">
                <label class="form-check-label" for="check3">
                  Supporting documents reviewed
                </label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="check4">
                <label class="form-check-label" for="check4">
                  Compliance requirements met
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <div class="d-flex justify-content-between w-100 align-items-center">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bx bx-x me-1"></i> Cancel
            </button>
            <div>
              <button type="button" @click="handleDecline" class="btn btn-danger me-2">
                <i class="bx bx-x-circle me-1"></i> Decline Permit
              </button>
              <button type="button" @click="handleApprove" class="btn btn-success">
                <i class="bx bx-check-circle me-1"></i> Approve Permit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ApprovalModal",
  props: {
    businessDetails: {
      type: Object,
      default: () => ({})
    },
    billDetails: {
      type: Object,
      default: () => ({})
    },
    comments: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      localComments: this.comments
    };
  },
  watch: {
    comments(newVal) {
      this.localComments = newVal;
    }
  },
  methods: {
    numberFormat(value) {
      if (!value) return '0';
      return new Intl.NumberFormat('en-US').format(value);
    },

    handleApprove() {
      // Optional: Add validation for approval
      const confirmed = confirm(
          `Are you sure you want to APPROVE this hygiene permit for ${this.businessDetails.businessName}?`
      );

      if (confirmed) {
        this.$emit('approve');
      }
    },

    handleDecline() {
      // Validate that comments are provided for decline
      if (!this.localComments || this.localComments.trim() === '') {
        alert('Please provide a reason for declining this permit in the comments section.');
        return;
      }

      const confirmed = confirm(
          `Are you sure you want to DECLINE this hygiene permit for ${this.businessDetails.businessName}?\n\nReason: ${this.localComments}`
      );

      if (confirmed) {
        this.$emit('decline');
      }
    }
  }
}
</script>

<style scoped>
.modal-header {
  border-bottom: none;
}

.modal-footer {
  border-top: 1px solid #eff2f7;
}

.alert {
  border-radius: 8px;
}

.card {
  transition: all 0.3s ease;
}

label {
  font-weight: 500;
  margin-bottom: 4px;
}

.form-control {
  border: 1px solid #ced4da;
  border-radius: 6px;
}

.form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.2rem rgba(85, 110, 230, 0.25);
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

.btn {
  padding: 0.5rem 1.5rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.btn-success {
  background-color: #34c38f;
  border-color: #34c38f;
}

.btn-success:hover {
  background-color: #2aa879;
  border-color: #2aa879;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(52, 195, 143, 0.3);
}

.btn-danger {
  background-color: #f46a6a;
  border-color: #f46a6a;
}

.btn-danger:hover {
  background-color: #f34141;
  border-color: #f34141;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(244, 106, 106, 0.3);
}

.btn-secondary {
  background-color: #74788d;
  border-color: #74788d;
}

.btn-secondary:hover {
  background-color: #5f6277;
  border-color: #5f6277;
}

.font-size-24 {
  font-size: 24px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.text-capitalize {
  text-transform: capitalize;
}

small {
  font-size: 0.875rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #eff2f7;
  padding: 12px 20px;
}

.card-body {
  padding: 20px;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

/* Alert Enhancements */
.alert-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.alert-success {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.alert-light {
  background-color: #fefefe;
  border: 1px solid #e9ecef;
}

.alert-heading {
  font-weight: 600;
}
</style>